import React, { useMemo } from "react";

import { MaterialResources } from "@/state/MaterialResources";
import { useResourcesState } from "@/state/resourcesState";
import { VertexView } from "./VertexView";
import { useEditorState } from "@/state/editorState";
import { ElementInstanceRenderer } from "./ElementInstanceRenderer";
import { MeshInstance } from "@/3d/modelInstance/MeshInstance";
import { ModelInstance } from "@/3d/modelInstance/ModelInstance";
import { MeshMaterials } from "@/3d/material/MeshMaterials";
import { DoubleSide, Euler, Vector3 } from "three";
import { GroupInstance } from "@/3d/modelInstance/GroupInstance";

export const EditorModelRenderer: React.FC = () => {
    const hiddenMeshes = useEditorState((state) => state.hiddenMeshes);
    const renderVertices = useEditorState((state) => state.renderVertices);
    const selectedMesh = useEditorState((state) => state.selectedMesh);

    const elementInstanceTest = useEditorState((state) => state.elementInstanceTest);

    const modelInstance = useEditorState((state) => state.modelInstance);

    const materials = useResourcesState((state) => state.materials);

    return (
        <group>
            {modelInstance &&
                Object.values(modelInstance.meshes)
                    .filter((m) => !hiddenMeshes.includes(m.raw.name) && !m.groupId)
                    .map((m) => (
                        <RawMeshRenderer
                            instance={m}
                            key={m.raw.name}
                            renderVertices={renderVertices}
                            isHidden={hiddenMeshes.includes(m.raw.name)}
                            isSelected={selectedMesh === m.raw.name}
                            model={modelInstance}
                            materials={materials}
                        />
                    ))}
            {modelInstance &&
                Object.values(modelInstance.groups).map((g) => (
                    <GroupRenderer
                        key={g.config.id}
                        instance={g}
                        model={modelInstance}
                        materials={materials}
                        renderVertices={renderVertices}
                    />
                ))}
            {elementInstanceTest &&
                modelInstance &&
                modelInstance.elementInstances.map((instance, index) => (
                    <ElementInstanceRenderer
                        key={index}
                        instance={instance}
                        model={modelInstance}
                    />
                ))}
        </group>
    );
};

type RawMeshRendererProps = {
    model: ModelInstance;
    instance: MeshInstance;
    materials: Record<string, MaterialResources>;
    renderVertices?: boolean;
    isHidden?: boolean;
    isSelected?: boolean;
    position?: Vector3;
    rotation?: Euler;
};

const RawMeshRenderer: React.FC<RawMeshRendererProps> = ({
    model,
    instance,
    materials,
    renderVertices = false,
    isHidden = false,
    isSelected = false,
    position,
    rotation,
}) => {
    const points = useMemo(() => instance.raw.getPoints(), [instance]);

    const selectedVertices = useEditorState((state) => state.selectedVertices);
    const selectedMeshMaterialName = useEditorState((state) => state.currentMeshMaterialName);

    return (
        <group position={position} rotation={rotation}>
            <mesh
                geometry={instance.geometry}
                key={instance.raw.name}
                visible={!isHidden}
                userData={{ type: "mesh", name: instance.raw.name }}
            >
                <MeshMaterials
                    model={model}
                    instance={instance}
                    materials={materials}
                    isSelected={isSelected}
                    selectedMeshMaterialName={selectedMeshMaterialName}
                />
            </mesh>
            {renderVertices &&
                !isHidden &&
                points.map((point, index) => (
                    <VertexView
                        key={index}
                        point={point}
                        meshName={instance.raw.name}
                        index={index}
                        isSelected={selectedVertices.some(
                            (v) => v.meshName === instance.raw.name && v.index === index
                        )}
                    />
                ))}
        </group>
    );
};

type GroupRendererProps = {
    model: ModelInstance;
    instance: GroupInstance;
    materials: Record<string, MaterialResources>;
    renderVertices?: boolean;
    isSelected?: boolean;
};

const GroupRenderer: React.FC<GroupRendererProps> = ({
    model,
    instance,
    materials,
    renderVertices = false,
    isSelected = false,
}) => {
    const hiddenMeshes = useEditorState((state) => state.hiddenMeshes);

    return (
        <group position={instance.position} rotation={instance.rotation}>
            <mesh>
                <boxGeometry args={[0.1, 0.1, 0.1]} />
                <meshBasicMaterial color="red" />
            </mesh>
            <mesh>
                <planeGeometry args={[0.1, 0.2]} />
                <meshBasicMaterial color="white" side={DoubleSide} />
            </mesh>
            <mesh rotation={[0, Math.PI / 2, 0]}>
                <planeGeometry args={[0.1, 0.2]} />
                <meshBasicMaterial color="white" side={DoubleSide} />
            </mesh>
            {Object.values(instance.meshes).map((m) => (
                <RawMeshRenderer
                    key={m.raw.name}
                    instance={m}
                    renderVertices={renderVertices}
                    isHidden={hiddenMeshes.includes(m.raw.name)}
                    isSelected={isSelected}
                    model={model}
                    materials={materials}
                    position={instance.meshPositions[m.raw.name]}
                    rotation={instance.meshRotations[m.raw.name]}
                />
            ))}
        </group>
    );
};
